import {defineStore} from "pinia";
import axios from "axios";
import {handleError, useInitializeStore} from "./storeHelper";
import {BTableOptions, serializeSortBy} from "@/components/BTable/BTable.types";
import {ref} from "vue";
import {
    BFieldSelection,
    BFilterableField,
    BFilterOptions,    
    includeOperations,
    listOperations,
    textOperations,
} from "@/components/BFilterEditor/BFilterEditor.types";
import type {AgencyDetailsViewSettings, AgencyItemDetails, AgencyLookup, Dataset, Vendor, VendorOri} from "@/types";
import {serializeFilters} from "@/components/BTable/HelperFunctions";
import {PagedList} from "@/audit.types";
import {useGlobalMessagesStore} from "./globalMessagesStore";
import {useAgencyTypeStore} from "./agencyTypesStore";
import {useCountyStore} from "./countyStore";
import {useVendorStore} from "./vendorStore";

export const useAgencyStore = defineStore("agencyStore", () => {
    const globalMessages = useGlobalMessagesStore();
    const apiUrl = "api/v1/agency/";
    const loading = ref<boolean>(false);
    const settings = ref<AgencyDetailsViewSettings>();
    const items = ref<AgencyItemDetails[]>();

    const filterOptions = ref<BFilterOptions>({
        filters: [],
    });

    const lookups = ref<AgencyLookup[]>();
    const datasets = ref<Dataset[]>();

    const tableOptions = ref<BTableOptions>({
        totalItemCount: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [
            {
                isDescending: false,
                key: "name",
            },
        ],
        filterBy: [],
        pagingMode: "withTotal",
    } as BTableOptions);

    const roles = ref<BFieldSelection[]>();
    const filterableFields = ref<BFilterableField[]>();

    useInitializeStore(async () => {
        const agencyTypeStore = useAgencyTypeStore();
        const countyStore = useCountyStore();
        const vendorStore = useVendorStore();

        vendorStore.tableOptions.itemsPerPage = 10000;
        await vendorStore.list();
        await agencyTypeStore.list();
        await countyStore.list();

        await listDatasets();

        filterableFields.value = [
            {
                property: "ori",
                label: "Ori",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "agencyName",
                label: "Agency Name",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "agencyType",
                label: "Agency Type",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: agencyTypeStore.items?.map((x) => ({value: x.code, text: x.label})),
            },
            {
                property: "county",
                label: "County",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: countyStore.items?.map((x) => ({value: x.code, text: x.name})),
            },
            {
                property: "activeDataCollection",
                label: "Active Data Collection",
                operations: includeOperations,
                editorComponent: "list-editor",
                editorOptions: datasets.value?.map((x) => ({value: x.code.toLocaleString(), text: x.label})),
            },
            {
                property: "vendor",
                label: "Vendor",
                operations: includeOperations,
                editorComponent: "list-editor",
                editorOptions: vendorStore.items?.map((x) => ({value: x.code, text: x.name})),
            },
        ];
    });

    const exportFile = async (fileType: string) => {
        try {
            loading.value = true;

            let filterParam = serializeFilters(filterOptions.value.filters, filterableFields.value!);
            let sortParam = serializeSortBy(tableOptions.value.sortBy);

            const response = await axios.get(`${apiUrl}export`, {
                responseType: "blob",
                params: {
                    fileType,
                    sort: sortParam,
                    filter: filterParam,
                },
            });

            const contentType = response.headers["content-type"] || "application/octet-stream";
            const blob = new Blob([response.data], {type: contentType as string});

            return blob;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const list = async () => {
        try {
            loading.value = true;

            let filterParam = serializeFilters(filterOptions.value.filters, filterableFields.value!);
            let sortParam = serializeSortBy(tableOptions.value.sortBy);
            console.log(filterOptions.value.filters, filterableFields.value);
            const response = await axios.get<PagedList<AgencyItemDetails>>(apiUrl, {
                params: {
                    pageSize: tableOptions.value.itemsPerPage,
                    page: tableOptions.value.page,
                    sort: sortParam,
                    filter: filterParam,
                },
            });
            items.value = response.data.data;
            tableOptions.value.totalItemCount = response.data.total;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const listLookups = async () => {
        try {
            loading.value = true;

            const response = await axios.get<AgencyLookup[]>(`${apiUrl}lookup`);

            lookups.value = response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const listDatasets = async () => {
        try {
            loading.value = true;

            const response = await axios.get<Dataset[]>(`${apiUrl}datasets`);

            datasets.value = response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const create = async (agency: AgencyItemDetails): Promise<void> => {
        try {
            loading.value = true;
            await axios.put(`${apiUrl}`, agency);
            globalMessages.push({message: "New agency details submitted.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const update = async (ori: string, agency: AgencyItemDetails): Promise<void> => {
        try {
            loading.value = true;
            await axios.post(`${apiUrl}${ori}`, agency);
            globalMessages.push({message: "New agency details submitted.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const deleteItem = async (vendorCode: string): Promise<void> => {
        try {
            loading.value = true;
            await axios.delete(`${apiUrl}${vendorCode}`);
            globalMessages.push({message: "Vendor deleted successfully.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const getById = async (ori: string): Promise<AgencyItemDetails> => {
        try {
            loading.value = true;
            const response = await axios.get<AgencyItemDetails>(`${apiUrl}${ori}`);
            return response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const getSettings = async (): Promise<void> => {
        try {
            loading.value = true;
            const response = await axios.get<AgencyDetailsViewSettings>(`${apiUrl}settings`);
            settings.value = response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const updateNotes = async (ori: string, note: string) => {
        try {
            loading.value = true;
            const response = await axios.post<boolean>(`${apiUrl}actions/${ori}/update-note`, {note});
            globalMessages.push({message: "Notes saved successfully.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const checkIfOriExists = async (ori: string): Promise<boolean> => {
        try {
            loading.value = true;
            const response = await axios.post<boolean>(`${apiUrl}check-ori`, {ori});
            return response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    return {
        list,
        getById,
        deleteItem,
        create,
        update,
        exportFile,
        listLookups,
        listDatasets,
        items,
        lookups,
        datasets,
        filterableFields,
        filterOptions,
        tableOptions,
        roles,
        loading,
        settings,
        updateNotes,
        getSettings,
        checkIfOriExists,
    };
});
